import { FC } from 'react';
import Head from 'next/head';
import { useTranslation } from 'react-i18next';

import { Link } from 'routes';
import { BreadcrumbList, ListItem, WithContext } from 'schema-dts';

import { getJobTypeDisplayName } from 'utils/job-type';
import type { VerticalPageJobType } from 'modules/job-types/types';

export type Crumb = { link: string; text: string };

function getStructuredData(crumbs: Array<Crumb>, language: 'en' | 'ja') {
  const listItems: Array<ListItem> = crumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    item: {
      '@id': `${`${language === 'en' ? '/en/' : '/'}`}${crumb.link}`,
      name: crumb.text,
    },
    position: 2 + index, // eslint-disable-line no-magic-numbers
  }));

  const structuredData: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        item: { '@id': `${language === 'en' ? '/en' : '/'}`, name: 'Zehitomo' },
        position: 1,
      },
      ...listItems,
    ],
  };

  return JSON.stringify(structuredData);
}

const getLanguagePrefix = (language: 'en' | 'ja') => (language === 'ja' ? '' : `/${language}`);
// TODO: Handle all crumb cases (category, vertical). Currently vertical top only :)
export const getCrumbs = ({
  jobType,
  blogPostName,
  blogTitle,
  language,
}: {
  blogPostName?: string;
  blogTitle?: string;
  jobType: VerticalPageJobType;
  language: 'en' | 'ja';
}) => {
  const languagePrefix = getLanguagePrefix(language);
  const jobTypeName = getJobTypeDisplayName(jobType, { shouldUseSEOName: true });

  const crumbs = [];
  let currentPath = languagePrefix;
  for (const jobCategory of jobType.jobCategories ?? []) {
    currentPath = `${currentPath}/${jobCategory.id}`;
    crumbs.push({
      link: currentPath,
      text: jobCategory.title,
    });
  }

  if (jobTypeName) {
    crumbs.push({
      isVertical: true,
      link: `${languagePrefix}/${jobType.categories.join('/')}/${jobType.id}`,
      text: jobTypeName,
    });
  }
  if (blogTitle && blogPostName) {
    crumbs.push({
      isVertical: true,
      link: `${languagePrefix}/${jobType.categories.join('/')}/${jobType.id}/blog/${blogPostName}`,
      text: blogTitle,
    });
  }
  return crumbs;
};

interface Props {
  align: 'center' | 'left' | 'right';
  crumbs: Array<Crumb>;
}

const Breadcrumbs: FC<Props> = ({ align, crumbs }) => {
  const {
    i18n: { language },
  } = useTranslation();

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          key="breadcrumbs-structured-data"
          dangerouslySetInnerHTML={{ __html: getStructuredData(crumbs, language as 'en' | 'ja') }}
        />
      </Head>

      <ol data-test-id="breadcrumbs">
        <li>
          <Link prefetch={false} route="/">
            <a>ゼヒトモ</a>
          </Link>
        </li>
        {crumbs.length > 0 &&
          crumbs.map((crumb, index) => (
            <li key={crumb.link}>
              {index < crumbs.length - 1 && (
                <Link prefetch={false} route={crumb.link}>
                  <a>
                    <span itemProp="name">{crumb.text}</span>
                  </a>
                </Link>
              )}
              {index === crumbs.length - 1 && <span>{crumb.text}</span>}
            </li>
          ))}
      </ol>

      <style jsx>
        {`
          ol {
            list-style-type: none;
            padding: 0;
            margin: ${align === 'center' ? '0 auto' : '0'};
            text-align: ${align};
            color: #666666;
          }

          li {
            display: inline-block;
            margin: 0 0.5rem var(--breadcrumb-mb);
            color: #666666;
          }

          li:first-child {
            padding-left: 0;
          }
          li:last-child {
            padding-right: 0;
          }

          li:after {
            content: '»';
            position: relative;
            right: -0.5rem;
          }

          li:last-child:after {
            display: none;
          }

          li a {
            display: inline-block;
            color: #666666;
          }
        `}
      </style>
    </>
  );
};
export default Breadcrumbs;
